import React, { ChangeEvent, useState } from 'react';
import { Box, Stack, Typography, Button, Paper } from '@mui/material';
import { UserPlus, UploadCloud, Download, FileText } from 'lucide-react';
import { toast } from '../../../../../../ui/toast';
import { InviteUserRequest } from '../../../../../../utils/types/assessment_types';
import { InviteSingleUser } from './invite_single_user/invite_single_user';

type InviteUserModalProps = {
  inviteUserForAssessment: (userRequest: InviteUserRequest) => void;
  inviteUsersFromCSV: (csvFile: File) => void;
};

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  inviteUserForAssessment,
  inviteUsersFromCSV,
}) => {
  const [addSingleUser, setAddSingleUser] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<File>();
  const [inviteUserDetails, setInviteUserDetails] = useState<InviteUserRequest>(
    {
      firstName: '',
      lastName: '',
      email: '',
      assessmentId: null,
    },
  );

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInviteUserDetails({
      ...inviteUserDetails,
      [event.target.name]: event.target.value,
    });
  };

  const addFileViaInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!uploadedFile) {
      const files = event.target.files;
      const splitFileString = files && files[0].name.split('.');
      if (
        files &&
        splitFileString &&
        splitFileString[splitFileString.length - 1] === 'csv'
      ) {
        setUploadedFile(files[0]);
      } else {
        toast.error('Invalid File type: CSV needed', {
          position: 'bottom-center',
        });
      }
    }
  };

  const handleDownload = () => {
    const fileUrl = process.env.PUBLIC_URL + '/files/users_invite_sample.csv';
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = 'users_invite_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (addSingleUser) {
    return (
      <InviteSingleUser
        inviteUserDetails={inviteUserDetails}
        handleInputChange={handleInputChange}
        inviteUserForAssessment={inviteUserForAssessment}
        setAddSingleUser={setAddSingleUser}
      />
    );
  }

  if (uploadedFile) {
    return (
      <Stack spacing={3} alignItems="center" sx={{ width: '100%', py: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          File Selected
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            width: '100%',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 2,
            bgcolor: 'action.hover',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FileText size={20} color="#1976d2" />
            <Typography variant="body2" fontWeight={600}>
              {uploadedFile.name}
            </Typography>
          </Stack>
          <Button
            size="small"
            color="error"
            onClick={() => setUploadedFile(undefined)}
            sx={{ textTransform: 'none' }}
          >
            Clear File
          </Button>
        </Paper>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => inviteUsersFromCSV(uploadedFile)}
          sx={{ textTransform: 'none', fontWeight: 600, py: 1.2, borderRadius: 1.5 }}
        >
          Submit File
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} alignItems="center" sx={{ width: '100%', py: 1 }}>
      <Typography variant="h6" fontWeight={700}>
        Choose Method
      </Typography>
      <input
        type="file"
        onChange={addFileViaInput}
        id="upload-file-input"
        style={{ display: 'none' }}
      />
      <Stack spacing={2} sx={{ width: '100%' }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<UserPlus size={18} />}
          onClick={() => setAddSingleUser(true)}
          sx={{ textTransform: 'none', fontWeight: 600, py: 1.2, borderRadius: 1.5 }}
        >
          Add Single User
        </Button>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          startIcon={<UploadCloud size={18} />}
          onClick={() => document.getElementById('upload-file-input')?.click()}
          sx={{ textTransform: 'none', fontWeight: 600, py: 1.2, borderRadius: 1.5 }}
        >
          Bulk Upload Users
        </Button>
        <Button
          variant="text"
          color="inherit"
          fullWidth
          startIcon={<Download size={18} />}
          onClick={handleDownload}
          sx={{ textTransform: 'none', fontWeight: 500, py: 1 }}
        >
          Download Sample CSV
        </Button>
      </Stack>
    </Stack>
  );
};
